import { useState } from "react";
import { useParams } from "react-router-dom";
import { observer } from "mobx-react-lite";

import { useSetOrRemoveBedrockKeys } from "../../../queries";
import LoadingSpinner from "../../../components/LoadingSpinner";

import { BedrockApiKeys } from "./types";
import { useApiKeyStore } from "./store/api-key-store";
import { RevealableInput } from "./RevealableInput";

import { useAuth } from "@/context/auth-context";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

const LoadingSkeleton = () => (
  <div className="max-w-[600px]">
    <div className="mt-1 flex flex-col gap-2">
      <div className="flex w-full flex-col gap-1">
        <Skeleton className="h-5 w-full max-w-[150px]" />
        <Skeleton className="h-9 w-full max-w-[600px]" />
      </div>
      <div className="flex w-full flex-col gap-1">
        <Skeleton className="h-5 w-full max-w-[150px]" />
        <Skeleton className="h-9 w-full max-w-[600px]" />
      </div>
      <div className="flex w-full flex-col gap-1">
        <Skeleton className="h-5 w-full max-w-[130px]" />
        <Skeleton className="h-9 w-full max-w-[600px]" />
      </div>
      <div className="mt-2 flex w-full">
        <Skeleton className="h-10 w-full max-w-[220px]" />
      </div>
    </div>
  </div>
);

interface BedrockSectionProps {
  onAfterSaveSuccess?: () => void;
}

const BedrockSection = observer(({ onAfterSaveSuccess }: BedrockSectionProps) => {
  const auth = useAuth();
  const { workspaceId } = useParams();
  const store = useApiKeyStore();
  const hasBedrockApiKey = store.getApiKey("bedrock");
  const isStoreLoading = store.isLoading;

  const [bedrockApiKey, setBedrockApiKey] = useState<BedrockApiKeys>({
    bedrock_aws_access_key: "",
    bedrock_aws_secret_key: "",
    bedrock_aws_region: "",
  });

  const { mutateAsync, isPending } = useSetOrRemoveBedrockKeys(auth?.userToken ?? "");

  const saveBedrockKeys = async (bedrockKeys: BedrockApiKeys | null) => {
    const response = await mutateAsync({
      ...bedrockKeys,
      workspace_id: Number(workspaceId),
    });
    if (response.success) {
      store.setApiKey("bedrock", !!bedrockKeys);
      store.setApiKeyConfig(
        "bedrock_aws_access_key_suffix",
        bedrockKeys?.bedrock_aws_access_key?.slice(-4) ?? "",
      );
      store.setApiKeyConfig("bedrock_aws_region", bedrockKeys?.bedrock_aws_region ?? "");
      setBedrockApiKey({
        bedrock_aws_access_key: "",
        bedrock_aws_secret_key: "",
        bedrock_aws_region: "",
      });
      if (bedrockKeys) {
        onAfterSaveSuccess?.();
      }
    }
  };

  const removeApiKey = async () => {
    await saveBedrockKeys(null);
  };

  if (isStoreLoading) {
    return <LoadingSkeleton />;
  }

  const isFormValid =
    bedrockApiKey.bedrock_aws_access_key &&
    bedrockApiKey.bedrock_aws_secret_key &&
    bedrockApiKey.bedrock_aws_region;

  const accessKeySuffix = store.getApiKeyConfig("bedrock_aws_access_key_suffix");
  const accessKeyDisplay = accessKeySuffix
    ? `••••••••••••••••••••${accessKeySuffix}`
    : "••••••••••••••••••••••••";

  return (
    <div className="mt-2 flex h-full flex-col">
      <div className="mt-1 flex max-w-[600px] flex-col gap-2">
        <Input
          label="Bedrock AWS Access Key"
          name="bedrock_aws_access_key"
          autoComplete="off"
          type={hasBedrockApiKey ? "text" : "password"}
          id="bedrock_aws_access_key"
          placeholder="Enter Bedrock AWS Access Key..."
          value={hasBedrockApiKey ? accessKeyDisplay : bedrockApiKey.bedrock_aws_access_key}
          readOnly={!!hasBedrockApiKey}
          className={hasBedrockApiKey ? "font-mono" : undefined}
          onChange={e =>
            setBedrockApiKey({
              ...bedrockApiKey,
              bedrock_aws_access_key: e.target.value,
            })
          }
        />
        <Input
          label="Bedrock AWS Secret Key"
          name="bedrock_aws_secret_key"
          autoComplete="off"
          type={hasBedrockApiKey ? "text" : "password"}
          id="bedrock_aws_secret_key"
          placeholder="Enter Bedrock AWS Secret Key..."
          value={
            hasBedrockApiKey ? "••••••••••••••••••••••••" : bedrockApiKey.bedrock_aws_secret_key
          }
          readOnly={!!hasBedrockApiKey}
          className={hasBedrockApiKey ? "font-mono" : undefined}
          onChange={e =>
            setBedrockApiKey({
              ...bedrockApiKey,
              bedrock_aws_secret_key: e.target.value,
            })
          }
        />
        <RevealableInput
          label="Bedrock AWS Region"
          name="bedrock_aws_region"
          id="bedrock_aws_region"
          placeholder="Enter Bedrock AWS Region..."
          value={
            hasBedrockApiKey
              ? store.getApiKeyConfig("bedrock_aws_region")
              : bedrockApiKey.bedrock_aws_region
          }
          readOnly={!!hasBedrockApiKey}
          onChange={e =>
            setBedrockApiKey({
              ...bedrockApiKey,
              bedrock_aws_region: e.target.value,
            })
          }
        />

        <div className="mt-2">
          {!hasBedrockApiKey ? (
            <Button
              onClick={() => saveBedrockKeys(bedrockApiKey)}
              variant="outline"
              disabled={!isFormValid || isPending}
            >
              {isPending ? <LoadingSpinner size={5} /> : "Save Bedrock Credentials"}
            </Button>
          ) : (
            <Button variant="destructiveOutline" onClick={removeApiKey} disabled={isPending}>
              Remove Bedrock Credentials
            </Button>
          )}
        </div>
      </div>
    </div>
  );
});

export default BedrockSection;
